// EntropyDashboardPage.jsx
import React, { useState, useEffect, useRef } from "react";
import EntropyDashboard from "../components/EntropyDashboard";
import { useMetrics } from "../context/MetricsContext";
import { freshness } from "../utils/freshness";
import "../styles/entropy-dashboard.css";

// --- Pastille d’état
const getModuleState = (value, prevValue) => {
  if (value === undefined || value === null) return "inactive";
  if (prevValue === undefined || prevValue === null) return "active";
  const delta = Math.abs(value - prevValue);
  if (delta < 0.01) return "stable";
  if (delta < 0.1) return "changing";
  return "volatile";
};

// --- Highlight sur changement
const useHighlightOnChange = (value, duration = 600) => {
  const [highlight, setHighlight] = useState(false);
  const prev = useRef(value);
  useEffect(() => {
    if (value !== prev.current) {
      setHighlight(true);
      prev.current = value;
      const t = setTimeout(() => setHighlight(false), duration);
      return () => clearTimeout(t);
    }
  }, [value, duration]);
  return highlight;
};

function ModuleRow({ mod, vals, entropy_total, percentage }) {
  const prev = useRef(vals.dynamic);
  const state = getModuleState(vals.dynamic, prev.current);
  prev.current = vals.dynamic;

  const uiChanged = useHighlightOnChange(vals.ui);
  const dynChanged = useHighlightOnChange(vals.dynamic);
  const totalChanged = useHighlightOnChange(vals.total);

  // Fraîcheur : rendu seul, aucune valeur touchée.
  const fresh = freshness(vals.age_seconds);

  return (
    <tr key={mod} style={{ opacity: fresh.opacity }} title={fresh.title}>
      <td style={{ display: "flex", alignItems: "center" }}>
        <span className={`state-dot ${state}`} />
        {mod}
        {fresh.suffix && (
          <span style={{ marginLeft: 6, fontSize: "0.7em", color: "#888" }}>
            {fresh.suffix}
          </span>
        )}
      </td>
      <td className={uiChanged ? "value-glow" : ""}>
        {Number(vals.ui || 0).toFixed(2)}
      </td>
      <td className={dynChanged ? "value-glow" : ""}>
        {Number(vals.dynamic || 0).toFixed(2)}
      </td>
      <td className={totalChanged ? "value-glow" : ""}>
        {Number(vals.total || 0).toFixed(2)}
        <div className="bar-container">
          <div
            className="bar"
            style={{
              width: `${percentage(vals.total, entropy_total)}%`,
            }}
          ></div>
        </div>
      </td>
    </tr>
  );
}

export default function EntropyDashboardPage() {
  const {
    entropy_total,
    entropy_ui_total,
    entropy_dynamic_total,
    scores,
    modules,
    connectionStatus,
  } = useMetrics();

  const [localModules, setLocalModules] = useState(modules || {});
  const [showInfoModal, setShowInfoModal] = useState(false); // ✅ Info Modal State

  // --- Rafraîchissement en temps réel (modules)
  useEffect(() => {
    setLocalModules(modules);
  }, [modules]);

  // --- Helpers visuels
  function percentage(value, total) {
    if (!total || total <= 0) return 0;
    return Math.min((value / total) * 100, 100);
  }

  return (
    <div className="entropy-dashboard-page">
      <div className="entropy-dashboard-page">
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
          <h1 style={{ margin: 0 }}>⚡ Entropy Dashboard</h1>
          <button
            onClick={() => setShowInfoModal(true)}
            style={{
              background: "rgba(0, 255, 255, 0.1)", border: "1px solid #0ff", color: "#0ff",
              borderRadius: "50%", width: "24px", height: "24px", fontSize: "0.9rem",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
            }}
            title="Comprendre l'Entropie"
          >
            i
          </button>
        </div>

        {/* 🔸 Indicateur de connexion SSE */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "1rem",
            padding: "6px 10px",
            borderRadius: "8px",
            background: "#1a1a1a",
            border: "1px solid #333",
            color:
              connectionStatus === "connected"
                ? "#00ff99"
                : connectionStatus === "reconnecting"
                  ? "#ffcc00"
                  : "#ff4444",
            boxShadow:
              connectionStatus === "connected"
                ? "0 0 8px #00ff99aa"
                : connectionStatus === "reconnecting"
                  ? "0 0 8px #ffaa00aa"
                  : "0 0 8px #ff0000aa",
            transition: "all 0.3s ease",
          }}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background:
                connectionStatus === "connected"
                  ? "#00ff99"
                  : connectionStatus === "reconnecting"
                    ? "#ffcc00"
                    : "#ff4444",
              boxShadow:
                connectionStatus === "connected"
                  ? "0 0 10px #00ff99"
                  : connectionStatus === "reconnecting"
                    ? "0 0 10px #ffaa00"
                    : "0 0 10px #ff0000",
            }}
          ></span>
          <span>
            {connectionStatus === "connected"
              ? "Connecté au flux d'entropie"
              : connectionStatus === "reconnecting"
                ? "Reconnexion en cours..."
                : "Connexion échouée"}
          </span>
        </div>

        <div className="entropy-main">
          <h2>
            Entropie Totale :{" "}
            <span className="entropy-value">
              {entropy_total.toFixed(2)}
            </span>
          </h2>
        </div>

        <EntropyDashboard entropy={entropy_total} />

        {/* --- Cartes récap --- */}
        <div className="gauges-row">
          <div className="card small">
            <h2>Entropie UI</h2>
            <div className="value ui">
              {entropy_ui_total.toFixed(2)}
            </div>
          </div>

          <div className="card small">
            <h2>Entropie Dynamique</h2>
            <div className="value dyn">
              {entropy_dynamic_total.toFixed(2)}
            </div>
          </div>

          <div className="card small">
            <h2>Score Total</h2>
            <div className="value total">
              {entropy_total.toFixed(2)}
            </div>
          </div>

          <div className="card medium">
            <h2 style={{ color: "#00e5ff" }}>Quantum Noise</h2>
            <div className="value">
              {scores.quantum_noise ? scores.quantum_noise.toFixed(4) : "—"}
            </div>
          </div>

          <div className="card medium">
            <h2 style={{ color: "#81d4fa" }}>Weather Feed</h2>
            <div className="value">
              {scores.weather_feed ? scores.weather_feed.toFixed(4) : "—"}
            </div>
          </div>
        </div>

        {/* --- Sommes globales --- */}
        <div style={{ marginBottom: 12 }}>
          <div>
            <strong>Somme Entropie Dynamique :</strong>{" "}
            {entropy_dynamic_total.toFixed(2)}
          </div>
          <div>
            <strong>Somme Entropie UI :</strong>{" "}
            {entropy_ui_total.toFixed(2)}
          </div>
        </div>

        {/* --- Entropies externes --- */}
        <div
          className="entropy-external-section"
          style={{
            marginTop: "1rem",
            padding: "0.8rem",
            background: "#0b0b0b",
            borderRadius: "8px",
          }}
        >
          <h3 style={{ color: "#00ffff", marginBottom: "0.5rem" }}>
            🌐 Entropies Externes
          </h3>
          <table style={{ width: "100%", color: "#ccc", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #333" }}>
                <th style={{ textAlign: "left" }}>Source</th>
                <th>Valeur Dynamique</th>
              </tr>
            </thead>
            {/* `scores` est une map plate module -> nombre (le total) ; seul
                `modules` porte le detail {ui, dynamic, total}. Lire
                `scores.<module>.dynamic` renvoyait donc toujours undefined,
                et le chaînage optionnel repliait silencieusement sur "—". */}
            <tbody>
              <tr>
                <td>Quantum Noise</td>
                <td>{modules?.quantum_noise?.dynamic?.toFixed(3) ?? "—"}</td>
              </tr>
              <tr>
                <td>Weather Feed</td>
                <td>{modules?.weather_feed?.dynamic?.toFixed(3) ?? "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* --- Tableau modules --- */}
        <div className="entropy-modules">
          {/* 🔹 Titre avec compteur global */}
          <div className="entropy-modules-header">
            <h3>Détails par module</h3>
            {(() => {
              const modulesArray = Object.values(localModules || {});
              const totalModules = modulesArray.length;
              const stableCount = modulesArray.filter(
                (v) => v && v.dynamic && Math.abs(v.dynamic - (v.prevDynamic || v.dynamic)) < 0.01
              ).length;

              const stableRatio = totalModules > 0 ? stableCount / totalModules : 0;
              let statusClass = "stability-good";
              let emoji = "🟢";

              if (stableRatio < 0.5) {
                statusClass = "stability-bad";
                emoji = "🟠";
              } else if (stableRatio < 0.8) {
                statusClass = "stability-mid";
                emoji = "🟡";
              }

              return (
                <span className={`global-stability ${statusClass}`}>
                  {emoji} Stables : {stableCount} / {totalModules}
                </span>
              );
            })()}
          </div>

          {/* 🧭 Légende d'état */}
          <div className="entropy-legend">
            <span><span className="state-dot stable" />Stable</span>
            <span><span className="state-dot changing" />Changeant</span>
            <span><span className="state-dot volatile" />Volatile</span>
            <span><span className="state-dot inactive" />Inactif</span>
          </div>

          <table className="entropy-table">
            <thead>
              <tr>
                <th>Module</th>
                <th style={{ color: "#4fc3f7" }}>UI (Σ)</th>
                <th style={{ color: "#81c784" }}>Dynamique (Σ)</th>
                <th style={{ color: "#fbc02d" }}>Total (Σ)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="summary-row">
                <td><strong>Somme</strong></td>
                <td style={{ color: "#4fc3f7" }}>{entropy_ui_total.toFixed(2)}</td>
                <td style={{ color: "#81c784" }}>{entropy_dynamic_total.toFixed(2)}</td>
                <td style={{ color: "#fbc02d" }}>{entropy_total.toFixed(2)}</td>
              </tr>

              {Object.entries(localModules || {}).map(([mod, vals]) => (
                <ModuleRow
                  key={mod}
                  mod={mod}
                  vals={vals}
                  entropy_total={entropy_total}
                  percentage={percentage}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ MODAL INFO ENTROPIE */}
      {showInfoModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          background: "rgba(0,0,0,0.8)", backdropFilter: "blur(5px)", zIndex: 9999,
          display: "flex", justifyContent: "center", alignItems: "center"
        }} onClick={() => setShowInfoModal(false)}>
          <div style={{
            background: "rgba(20, 30, 50, 0.95)", border: "1px solid #0ff", padding: "30px", borderRadius: "16px",
            maxWidth: "600px", width: "90%", boxShadow: "0 0 50px rgba(0, 255, 255, 0.2)",
            color: "#fff", position: "relative", maxHeight: "90vh", overflowY: "auto"
          }} onClick={e => e.stopPropagation()}>

            <h2 style={{ borderBottom: "1px solid #333", paddingBottom: "10px", marginBottom: "20px", color: "#0ff" }}>
              Le Cœur du Système (Entropie)
            </h2>

            <div style={{ textAlign: "left", lineHeight: "1.6" }}>
              <h3 style={{ color: "#ffd369", marginTop: "15px" }}>1. Ce que vous voyez</h3>
              <p style={{ color: "#ddd" }}>
                Ces graphiques visualisent en direct le <strong>"Bruit de Fond"</strong> de l'univers. Nous captons des données atmosphériques, thermiques et radio pour générer un hasard pur.
              </p>

              <h3 style={{ color: "#ffd369", marginTop: "15px" }}>2. Pourquoi est-ce unique ?</h3>
              <p style={{ color: "#ddd" }}>
                Un ordinateur classique ne sait faire que du "faux hasard" (pseudo-aléatoire). Ici, nous injectons du <strong>chaos physique réel</strong>. C'est la seule garantie mathématique d'imprévisibilité totale.
              </p>

              <h3 style={{ color: "#ffd369", marginTop: "15px" }}>3. L'usage</h3>
              <p style={{ color: "#ddd" }}>
                Cette source alimente le Générateur de Token et les Certificats QUID. C'est la matière première de votre sécurité.
              </p>
            </div>

            <button onClick={() => setShowInfoModal(false)} style={{
              marginTop: "25px", width: "100%", padding: "12px", background: "#333", color: "#fff",
              border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold"
            }}>
              Fermer
            </button>

          </div>
        </div>
      )}
    </div>
  );
}
